import { Component, EventEmitter, inject, OnInit, Output } from '@angular/core';
import { AsyncPipe, DatePipe } from '@angular/common';
import { SignaturePadComponent } from '../signature-pad/signature-pad.component';
import { catchError, Observable, of } from 'rxjs';
import { ServoProductNameTranslations } from '@base/models/enum/insurance-type.enum';
import { SignatureService } from '../../services/signature.service';
import { BrokernetProductName, PublicServoAntragSignInfoDto, PublicServoAntragSignSubmitDto } from '@base/backend';

@Component({
  selector: 'app-signature',
  standalone: true,
  imports: [
    AsyncPipe,
    SignaturePadComponent,
    DatePipe
  ],
  templateUrl: './signature.component.html',
  styleUrl: './signature.component.scss'
})
export class SignatureComponent implements OnInit {
  private readonly signatureService = inject(SignatureService);

  @Output() submitted = new EventEmitter<void>();

  currentDate = new Date();

  signatureData$: Observable<PublicServoAntragSignInfoDto> | null = null;

  signatureBase64: string | null = null;
  showSignatureInvalidError = false;

  isLoading = false;
  showError = false;

  private oneTimeAccessToken = '';

  ngOnInit() {
    const url = new URL(window.location.href);

    if (!url.searchParams.has('id')) {
      this.signatureData$ = null;
      console.error('URL param has invalid id ', url);
      this.showError = true;
      return;
    }

    if (!url.searchParams.has('oneTimeAccessToken')) {
      this.signatureData$ = null;
      console.error('URL param has invalid oneTimeAccessToken ', url);
      this.showError = true;
      return;
    }

    const contractId = url.searchParams.get('id')!;
    this.oneTimeAccessToken = url.searchParams.get('oneTimeAccessToken')!;

    this.signatureData$ = this.signatureService
      .getSignatureInformation(contractId, this.oneTimeAccessToken)
      .pipe(catchError((err) => {
        this.showError = true;

        if (err) {
          console.error(err);
        }

        return of();
      }));
  }

  getInsuranceTranslation(type: BrokernetProductName | undefined): string {
    if (!type)
      return '';

    return ServoProductNameTranslations.getTypeTranslation(type);
  }

  downloadDocument(insuranceId: string | undefined): void {
    if (!insuranceId)
      return;

    this.signatureService
      .downloadOffert(insuranceId, this.oneTimeAccessToken)
      .subscribe(blob => {
        if (isIOS) {
          // For iOS devices, open the PDF in a new tab
          window.open(URL.createObjectURL(blob), '_blank');
          return;
        }

        const a = document.createElement('a');
        a.href = URL.createObjectURL(blob);
        a.download = `Versicherungsantrag.pdf`;

        document.body.appendChild(a); // Append to the document
        a.click(); // Trigger download
        a.remove(); // Clean up
      });
  }

  submit(insuranceId: string | undefined): void {
    this.showSignatureInvalidError = false;

    if (!insuranceId)
      return;

    if (this.signatureBase64 === null) {
      this.showSignatureInvalidError = true;
      return;
    }

    this.isLoading = true;

    const submitDto: PublicServoAntragSignSubmitDto = {
      customerSignatureBase64Encoded: this.signatureBase64,
      oneTimeAccessToken: this.oneTimeAccessToken
    };

    this.signatureService
      .save(insuranceId, submitDto)
      .subscribe({
        next: () => this.submitted.emit(),
        complete: () => this.isLoading = false
      });
  }
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !(window as any).MSStream;
